import { useId, useState } from 'react';
import { Segmented, Tooltip } from 'antd';
import type { TableProps } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Ban } from 'lucide-react';

import { DataTable, MenuThaoTac, Tile, TileHeader } from '../../../components/shared';
import { Badge, Button, Dialog, Empty, Field } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { loiTheoTruong } from '../../../lib/errors';
import { useToast } from '../../../lib/toast';
import { BADGE_CHAN_TU_DONG, TRAN_CHAN_IP } from '../constants';
import { useDanhSachChan, useThemChan, useXoaChan } from '../hooks/useBaoMat';
import { THOI_HAN_CHAN, THOI_HAN_MAC_DINH, conLai, laTuDong, phutCuaThoiHan } from '../nghiep-vu/chan-ip';
import type { MaThoiHan } from '../nghiep-vu/chan-ip';
import { ipThuocCidr } from '../nghiep-vu/cidr';
import { docDanhSachIp } from '../nghiep-vu/danh-sach-ip';
import type { DongChanIp, PhamViChan } from '../types';
import { ChuPhuPhamVi } from './form/ChuPhuPhamVi';
import { ONhapDanhSachIp, cauLoiDong } from './form/ONhapDanhSachIp';

/** Lý do tối đa của một dòng blocklist (`reason` của `IpBlocklistEntryCreate`). */
const LY_DO_TOI_DA = 500;

/** Chữ khác nhau giữa blocklist nền tảng và blocklist của tenant; còn lại dùng chung. */
const CHU: Record<PhamViChan['loai'], Record<'tieuDe' | 'canhBao' | 'rong' | 'rongMo' | 'loi' | 'xacNhanGoThan', KhoaDich>> = {
  'nen-tang': {
    tieuDe: 'chinhSach.blocklist.tieuDe',
    canhBao: 'chinhSach.blocklist.canhBao',
    rong: 'chinhSach.blocklist.rong',
    rongMo: 'chinhSach.blocklist.rongMo',
    loi: 'chinhSach.blocklist.loi',
    xacNhanGoThan: 'chinhSach.blocklist.xacNhanGoThan',
  },
  tenant: {
    tieuDe: 'baoMat.chan.tieuDe',
    canhBao: 'baoMat.chan.canhBao',
    rong: 'baoMat.chan.rong',
    rongMo: 'baoMat.chan.rongMo',
    loi: 'chinhSach.blocklist.loi',
    xacNhanGoThan: 'baoMat.chan.xacNhanGoThan',
  },
};

/**
 * Blocklist IP có hạn (ADR-0059 mục 6.4, ADR-0090 mục 3, ADR-0098), **một** bảng cho hai nơi: blocklist nền tảng ở màn
 * Chính sách nền tảng và blocklist của một tenant ở thẻ Truy cập IP.
 *
 * - Thêm bằng ô nhập theo dòng kiểu aaPanel: mỗi dòng một IP hoặc CIDR, lý do sau `#` (bỏ trống thì ghi lý do chung),
 *   một thời hạn cho cả lượt — 1 giờ, 24 giờ (mặc định), 7 ngày hoặc vĩnh viễn. Nút nói số địa chỉ sắp chặn. Bấm là
 *   chặn ngay, không qua thanh lưu: mỗi dòng là một lần `POST`, dòng lỗi ở lại trong ô kèm câu lỗi của máy chủ.
 * - Không tự khoá mình (`kiemTuKhoa`): dòng chứa IP của chính người đang sửa bị chặn ngay ở ô nhập; backend cũng từ
 *   chối (blocklist nền tảng với mọi người, blocklist tenant với Tenant Admin).
 * - Dòng chặn tự động mang nhãn phân loại "Tự động", gỡ sớm được như dòng chặn tay, qua menu thao tác của dòng.
 */
export function BangChanIp({
  phamVi,
  duocSua,
  ipHienTai = null,
  kiemTuKhoa = false,
}: {
  phamVi: PhamViChan;
  duocSua: boolean;
  ipHienTai?: string | null;
  /** Chặn dòng chứa `ipHienTai`: chặn dải đó là tự khoá mình. */
  kiemTuKhoa?: boolean;
}) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const chu = CHU[phamVi.loai];
  const q = useDanhSachChan(phamVi);
  const them = useThemChan(phamVi);
  const xoa = useXoaChan(phamVi);
  const oNhapId = useId();

  const [van, setVan] = useState('');
  const [thoiHan, setThoiHan] = useState<MaThoiHan>(THOI_HAN_MAC_DINH);
  /** Lỗi máy chủ của lượt chặn vừa rồi, theo số dòng của ô nhập. Gõ lại thì bỏ. */
  const [loiMay, setLoiMay] = useState<Array<{ dong: number; cau: string }>>([]);
  const [dangChan, setDangChan] = useState(false);
  const [hoiXoa, setHoiXoa] = useState<DongChanIp | null>(null);

  const bayGio = Date.now();
  const dangCo = (q.data?.items ?? []).filter((d) => conLai(d.expires_at, bayGio).loai !== 'het-han');
  const ipCuaBan = kiemTuKhoa ? ipHienTai : null;
  const kq = docDanhSachIp(van, {
    toiDa: Math.max(0, TRAN_CHAN_IP - dangCo.length),
    camTatCa: true,
    ghiChuToiDa: LY_DO_TOI_DA,
    ipCuaBan,
    daCo: dangCo.map((d) => d.cidr),
  });
  const loiDong = [
    ...kq.loi.map((l) => ({
      dong: l.dong,
      cau: cauLoiDong(t, l, { ipCuaBan, max: TRAN_CHAN_IP, ghiChuToiDa: LY_DO_TOI_DA }),
    })),
    ...loiMay,
  ].sort((a, b) => a.dong - b.dong);
  const soChan = kq.muc.length;
  const chanDuoc = soChan > 0 && kq.loi.length === 0 && !dangChan;

  const doiVan = (v: string) => {
    setVan(v);
    setLoiMay([]);
  };

  const chan = async () => {
    if (!chanDuoc) return;
    setDangChan(true);
    const dong = van.split(/\r?\n/);
    const conLaiDong: string[] = [];
    const loiMoi: Array<{ dong: number; cau: string }> = [];
    let daChan = 0;
    for (const m of kq.muc) {
      try {
        await them.mutateAsync({
          vao: {
            cidr: m.cidr,
            reason: m.ghiChu ?? t('baoMat.chan.lyDoMacDinh'),
            duration_minutes: phutCuaThoiHan(thoiHan),
          },
          khoaLap: crypto.randomUUID(),
        });
        daChan += 1;
      } catch (e) {
        // Dòng lỗi ở lại trong ô, đánh số lại theo vị trí mới, kèm câu của máy chủ (đã dịch theo Accept-Language)
        conLaiDong.push(dong[m.dong - 1] ?? m.cidr);
        const theoTruong = loiTheoTruong(e);
        const cau = theoTruong['/cidr'] ?? theoTruong['/reason'] ?? dichLoi(e);
        loiMoi.push({ dong: conLaiDong.length, cau: t('baoMat.dsIp.loiMayChu', { dong: conLaiDong.length, loi: cau }) });
      }
    }
    setVan(conLaiDong.join('\n'));
    setLoiMay(loiMoi);
    setDangChan(false);
    if (daChan > 0) toast.success(t('baoMat.chan.daChanSo', { so: soVN(daChan, locale) }));
  };

  const chuConLai = (dong: DongChanIp): string => {
    const c = conLai(dong.expires_at, bayGio);
    switch (c.loai) {
      case 'vinh-vien':
        return t('baoMat.chan.vinhVien');
      case 'het-han':
        return t('caiDat.cidr.daHetHan');
      case 'phut':
        return t('baoMat.chan.conPhut', { so: c.so });
      case 'gio':
        return t('baoMat.chan.conGio', { so: c.so });
      case 'ngay':
        return t('baoMat.chan.conNgay', { so: c.so });
    }
  };

  const cot: TableProps<DongChanIp>['columns'] = [
    {
      title: t('caiDat.cidr.cotCidr'),
      dataIndex: 'cidr',
      width: 190,
      render: (v: string) => (
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="flex min-w-0 flex-col">
            <span className="break-all font-mono text-xs text-content-primary">{v}</span>
            <ChuPhuPhamVi cidr={v} />
          </span>
          {ipHienTai && ipThuocCidr(ipHienTai, v) && (
            <Badge variant="primary">{t('caiDat.cidr.chuaIpCuaBan')}</Badge>
          )}
        </div>
      ),
    },
    {
      title: t('chinhSach.blocklist.cotLyDo'),
      dataIndex: 'reason',
      // Tổng bề rộng các cột vừa khung 1280px để cột thao tác không bị đẩy ra ngoài; lý do dài thì xuống dòng
      width: 240,
      render: (v: string) => <span className="line-clamp-2 text-sm text-content-secondary">{v}</span>,
    },
    {
      title: t('baoMat.chan.cotNguon'),
      dataIndex: 'source',
      width: 110,
      render: (_v: unknown, row) =>
        laTuDong(row) ? (
          <Tooltip title={t('baoMat.chan.tuDongMo')}>
            <span>
              <Badge variant={BADGE_CHAN_TU_DONG.variant}>{t(BADGE_CHAN_TU_DONG.khoa)}</Badge>
            </span>
          </Tooltip>
        ) : (
          <Badge variant="neutral">{t('baoMat.chan.thuCong')}</Badge>
        ),
    },
    {
      title: t('baoMat.chan.cotThoiHan'),
      dataIndex: 'expires_at',
      width: 170,
      render: (v: string | null, row) => (
        <div className="flex min-w-0 flex-col">
          <span
            className={
              conLai(v, bayGio).loai === 'het-han'
                ? 'text-xs text-content-muted'
                : 'text-xs font-medium text-content-primary'
            }
          >
            {chuConLai(row)}
          </span>
          {v && (
            <span className="whitespace-nowrap text-2xs text-content-muted">
              {t('baoMat.chan.hetLuc', { luc: ngayGioVN(v, locale) })}
            </span>
          )}
        </div>
      ),
    },
    {
      title: t('chinhSach.blocklist.cotTao'),
      width: 180,
      render: (_v, row) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-xs text-content-secondary">
            {row.created_by ?? t('caiDat.lichSu.heThong')}
          </span>
          <span className="text-2xs text-content-muted">{ngayGioVN(row.created_at, locale)}</span>
        </div>
      ),
    },
    ...(duocSua
      ? [
          {
            title: <span className="sr-only">{t('chung.thaoTac')}</span>,
            width: 72,
            align: 'center' as const,
            render: (_v: unknown, row: DongChanIp) => (
              <MenuThaoTac
                tenBanGhi={row.cidr}
                muc={[
                  {
                    khoa: 'go',
                    nhan: t('chinhSach.blocklist.go'),
                    icon: <DeleteOutlined />,
                    nhom: 'pha-huy',
                    onChon: () => {
                      xoa.reset();
                      setHoiXoa(row);
                    },
                  },
                ]}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <Tile className="overflow-hidden">
      <TileHeader icon={<Ban size={16} />} tone="primary" title={t(chu.tieuDe)} />

      {/* Danh sách chưa tải được thì chưa biết dòng nào đã có, còn thêm được bao nhiêu: ẩn ô thêm */}
      {duocSua && !q.error && (
        <div className="space-y-4 border-b border-default px-5 py-4">
          <ONhapDanhSachIp
            id={oNhapId}
            giaTri={van}
            onDoi={doiVan}
            chiDoc={dangChan}
            kq={kq}
            loiDong={loiDong}
            max={TRAN_CHAN_IP}
            viDu={t('baoMat.chan.viDu')}
            huongDan={[t('baoMat.chan.huongDanLyDo'), t('baoMat.chan.huongDanTuKhoa')]}
            dauTren={
              <label htmlFor={oNhapId} className="block text-sm font-medium text-content-primary">
                {t('baoMat.chan.oNhap')}
              </label>
            }
          />
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <Field
              label={t('baoMat.chan.oThoiHan')}
              // Chỉ "Vĩnh viễn" cần nói hậu quả; thời hạn có hạn thì nhãn đã đủ nghĩa
              hint={thoiHan === 'vinh-vien' ? t('baoMat.chan.thoiHanVinhVienGoiY') : undefined}
            >
              {(p) => (
                <Segmented<MaThoiHan>
                  id={p.id}
                  aria-describedby={p['aria-describedby']}
                  className="segmented-xuong-dong max-w-full"
                  value={thoiHan}
                  onChange={setThoiHan}
                  disabled={dangChan}
                  options={THOI_HAN_CHAN.map((th) => ({
                    value: th.ma,
                    label: t(`baoMat.chanThoiHan.${th.ma}` as KhoaDich),
                  }))}
                />
              )}
            </Field>
            <Button variant="destructive" size="md" icon={<Ban size={14} />} disabled={!chanDuoc} loading={dangChan} onClick={() => void chan()}>
              {soChan > 0 ? t('baoMat.chan.nhanChanSo', { so: soVN(soChan, locale) }) : t('baoMat.chan.nhanChan')}
            </Button>
          </div>
          <p role="note" className="rounded border border-warning-200 bg-warning-50 px-3 py-2.5 text-xs leading-relaxed text-warning-700">
            {t(chu.canhBao)}
          </p>
        </div>
      )}

      {q.error ? (
        <div role="alert" className="flex items-center justify-between gap-3 px-5 py-4">
          <span className="text-sm text-danger-700">{t(chu.loi, { loi: dichLoi(q.error) })}</span>
          <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
            {t('chung.thuLai')}
          </Button>
        </div>
      ) : (
        <DataTable<DongChanIp>
          caption={t(chu.tieuDe)}
          rowKey="id"
          dataSource={q.data?.items ?? []}
          columns={cot}
          loading={q.isLoading}
          size="middle"
          pagination={false}
          locale={{
            emptyText: q.isLoading ? ' ' : <Empty icon={Ban} title={t(chu.rong)} description={t(chu.rongMo)} />,
          }}
        />
      )}

      <Dialog
        open={hoiXoa !== null}
        onClose={() => setHoiXoa(null)}
        title={hoiXoa ? t('chinhSach.blocklist.xacNhanGoTieuDe', { cidr: hoiXoa.cidr }) : ''}
        width={480}
        destroyOnHidden
        footer={
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={() => setHoiXoa(null)}>
              {t('chung.huy')}
            </Button>
            <Button
              variant="destructive"
              size="md"
              loading={xoa.isPending}
              onClick={() =>
                hoiXoa &&
                xoa.mutate(hoiXoa.id, {
                  onSuccess: () => {
                    toast.success(t('chinhSach.blocklist.daGo', { cidr: hoiXoa.cidr }));
                    setHoiXoa(null);
                  },
                })
              }
            >
              {hoiXoa ? t('chinhSach.blocklist.nhanGo', { cidr: hoiXoa.cidr }) : ''}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 pt-2">
          <p className="text-sm leading-relaxed text-content-secondary">{t(chu.xacNhanGoThan)}</p>
          {hoiXoa && laTuDong(hoiXoa) && (
            <p className="text-xs leading-relaxed text-content-muted">{t('baoMat.chan.goSomTuDong')}</p>
          )}
          {xoa.error && (
            <div role="alert" className="rounded border border-danger-200 bg-danger-50 px-3 py-2.5 text-xs text-danger-700">
              {dichLoi(xoa.error)}
            </div>
          )}
        </div>
      </Dialog>
    </Tile>
  );
}
